import { QrCodeIcon } from '@heroicons/react/24/solid'

import { anim } from '../../motion'
import { COLORS, Shape } from '../../primitives'
import { Envelope, Flight, MiniPhone, Person, Walker } from '../../v3/figures'
import { IsoBox, U, iso, onWallA, pts } from '../../v3/iso'
import { Panel } from '../../v3/panels'
import { Anchor, Caption, Tag, timed } from '../kit'
import { cam, cameraAt } from '../timeline'

/**
 * Extra „Messe“: Mara geht aus dem Büro in die Messehalle nebenan, stellt
 * am Aivy-Stand einen QR-Code auf, Talente spielen Aivy® Attract und sehen,
 * welche Stelle passt. Danach geht sie zurück, die Bewerbungen fliegen los.
 */

const HALL = { x: 30, y: 0, w: 14, d: 13, h: 4.6 }
const STAND = { x: 33, y: 1, w: 6 }
const QR_AT = { x: 40.4, y: 4.4 }
const MARA_LOOK = { body: 'blue', skin: '#E0AC69', hair: '#5A3825' }

const WAY_OUT = [
  { x: 10.9, y: 9.9 },
  { x: 13.4, y: 9.9 },
  { x: 16.2, y: 6.4 },
  { x: 25.6, y: 6.4 },
  { x: 31, y: 6.5 },
  { x: 33.4, y: 2.6 },
  { x: 35.8, y: 2.5 }
]

const maraPath = [
  ...timed(WAY_OUT, 0.06, 0.34),
  ...timed([...WAY_OUT].reverse(), 0.86, 0.98)
]

const VISITORS = [
  {
    look: { body: 'pink', skin: '#F1D3B3', hair: '#7A4A2A' },
    to: { x: 37.6, y: 7 },
    job: 'Customer Success',
    top: true
  },
  {
    look: { body: '#4461D3', skin: '#8D5524', hair: '#182033' },
    to: { x: 40, y: 8.6 },
    job: 'Vertrieb'
  },
  {
    look: { body: 'yellow', skin: '#C68B59', hair: '#2B1D14' },
    to: { x: 42.4, y: 6.2 },
    job: 'Customer Success',
    top: true
  },
  {
    look: { body: '#9FE0C2', skin: '#E0AC69', hair: '#5A3825' },
    to: { x: 36.4, y: 10.4 },
    job: 'Innendienst'
  },
  {
    look: { body: '#C9A7F2', skin: '#F2C9A5', hair: '#E6C27A' },
    to: { x: 41.8, y: 10.6 },
    job: 'Vertrieb'
  }
]

const visitorPath = (v, i) => {
  const start = { x: 34 + i * 2.2, y: 15.5 }
  const leave = { x: 36 + i * 1.6, y: 15.5 }
  return [
    ...timed([start, v.to], 0.34 + i * 0.025, 0.44 + i * 0.025),
    ...timed([v.to, leave], 0.88 + i * 0.012, 0.96 + i * 0.012)
  ]
}

const DOOR = iso(22.2, 0.3, 2.2)
const LETTERS = [0, 1, 2, 3].map((i) => {
  const from = iso(39 + i, 6 + (i % 2), 1.6)
  return {
    rot: i * 12 - 18,
    legs: [
      {
        from,
        to: DOOR,
        at: [0.86 + i * 0.02, 0.97 + i * 0.008],
        arc: 160,
        spin: 90
      }
    ]
  }
})

// ---------- Halle ----------

function Hall() {
  const { x, y, w, d, h } = HALL
  const wallA = [iso(x, y), iso(x + w, y), iso(x + w, y, h), iso(x, y, h)]
  const wallB1 = [iso(x, y), iso(x, 5), iso(x, 5, h), iso(x, y, h)]
  const wallB2 = [iso(x, 8.2), iso(x, d), iso(x, d, h), iso(x, 8.2, h)]
  const lintel = [
    iso(x, 5, 3.4),
    iso(x, 8.2, 3.4),
    iso(x, 8.2, h),
    iso(x, 5, h)
  ]
  return (
    <g>
      {/* Weg vom Büro zur Halle */}
      <IsoBox
        x={26}
        y={5.3}
        z={-0.8}
        w={4}
        d={2.4}
        h={0.8}
        top='#E3E8F0'
        left='#C3CDE3'
        right='#AFBBD6'
      />
      <IsoBox
        x={x}
        y={y}
        z={-0.8}
        w={w}
        d={d}
        h={0.8}
        top='#F7F6F6'
        left='#D3D6DE'
        right='#BFC4D0'
      />
      <polygon points={pts(wallA)} fill='#FFFFFF' />
      <polygon points={pts(wallB1)} fill='#EEF2FB' />
      <polygon points={pts(wallB2)} fill='#EEF2FB' />
      <polygon points={pts(lintel)} fill='#EEF2FB' />

      {/* Nachbarstände */}
      <IsoBox
        x={41}
        y={1}
        w={2.6}
        d={0.4}
        h={3}
        top='#FDE6EF'
        left={COLORS.pink}
        right='#E8679A'
      />
      <IsoBox
        x={31.2}
        y={9.6}
        w={2.4}
        d={0.4}
        h={2.6}
        top='#FFF3D6'
        left={COLORS.yellow}
        right='#E8A21C'
      />

      {/* Aivy-Stand */}
      <IsoBox
        x={STAND.x}
        y={STAND.y}
        w={STAND.w}
        d={0.4}
        h={3.4}
        top='#6E9BFF'
        left={COLORS.blue}
        right='#2F62E0'
      />
    </g>
  )
}

function Counter() {
  return (
    <IsoBox
      x={34.2}
      y={3.1}
      w={3.4}
      d={0.9}
      h={1}
      top='#FFFFFF'
      left='#DDE3EF'
      right='#CBD3E3'
    />
  )
}

function QrStand() {
  return (
    <g>
      <IsoBox
        x={QR_AT.x}
        y={QR_AT.y}
        w={0.16}
        d={0.16}
        h={1.6}
        top={COLORS.ink}
        left={COLORS.ink}
        right={COLORS.ink}
      />
      <IsoBox
        x={QR_AT.x - 0.75}
        y={QR_AT.y - 0.05}
        z={1.6}
        w={1.66}
        d={0.12}
        h={1.7}
        top='#FFFFFF'
        left='#FFFFFF'
        right='#DDE3EF'
      />
    </g>
  )
}

export function MesseWorld() {
  return (
    <>
      <svg className='off-svg kx-svg' aria-hidden='true'>
        <Hall />
        <Counter />
        <QrStand />

        {/* Mara auf dem Weg, ersetzt die Mara am Schreibtisch */}
        <g className='kx-mara'>
          <Walker path={maraPath}>
            <Person {...MARA_LOOK} happy={[0.5, 0.6]} />
          </Walker>
        </g>

        {VISITORS.map((v, i) => (
          <g
            key={i}
            style={anim({
              at: [0.33 + i * 0.025, 0.35 + i * 0.025],
              out: [0.94, 0.98],
              y: 0,
              oy: 0
            })}
          >
            <Walker path={visitorPath(v, i)}>
              <Person {...v.look} happy={[0.6 + i * 0.02, 0.66 + i * 0.02]} />
              <g transform='translate(18 -48) scale(0.8)'>
                <g
                  className='off-a'
                  style={anim({
                    at: [0.46 + i * 0.02, 0.5 + i * 0.02],
                    out: [0.62, 0.66],
                    s: 0.3,
                    y: 6
                  })}
                >
                  <MiniPhone shape={['tri', 'sq', 'dot'][i % 3]} color='blue' />
                </g>
              </g>
            </Walker>
          </g>
        ))}

        <Flight
          items={LETTERS}
          render={(it) => (
            <g transform={`rotate(${it.rot})`}>
              <Envelope />
            </g>
          )}
        />
      </svg>

      <div className='off-overlay'>
        {/* Schilder an Halle und Stand */}
        <div
          className='kx-sign'
          style={{
            width: 7 * U,
            transform: onWallA(iso(HALL.x + 3.4, 0, 4.2))
          }}
        >
          Karrieremesse
        </div>
        <div
          className='kx-stand'
          style={{
            width: STAND.w * U,
            transform: onWallA(iso(STAND.x, STAND.y + 0.4, 3.2))
          }}
        >
          <b>aivy</b>
          <span>Welcher Job passt zu dir?</span>
          <i>
            <Shape kind='tri' color='yellow' size={14} />
            <Shape kind='sq' color='green' size={12} />
            <Shape kind='dot' color='pink' size={12} />
          </i>
        </div>
        <div
          className='kx-qr'
          style={{
            width: 1.66 * U,
            transform: onWallA(iso(QR_AT.x - 0.75, QR_AT.y + 0.07, 3.25))
          }}
        >
          <QrCodeIcon width={40} height={40} />
          <span>Scan mich</span>
        </div>

        <Tag
          at3={[QR_AT.x, QR_AT.y, 3.4]}
          dy={16}
          dx={70}
          show={[0.34, 0.4]}
          hide={[0.58, 0.62]}
          tone='blue'
        >
          Aivy® Attract
        </Tag>

        {VISITORS.map((v, i) => (
          <Anchor key={i} at3={[v.to.x, v.to.y, 0]} dy={78}>
            <div
              className='kx-match'
              style={anim({
                at: [0.62 + i * 0.03, 0.67 + i * 0.03],
                out: [0.84, 0.88],
                y: 10,
                s: 0.6
              })}
            >
              {v.top && <em>Passt sehr gut</em>}
              {v.job}
            </div>
          </Anchor>
        ))}
      </div>
    </>
  )
}

// ---------- Bildschirm ----------

const MATCHES = [
  { job: 'Customer Success Manager:in', value: 91, top: true },
  { job: 'Vertrieb Innendienst', value: 76 },
  { job: 'Teamassistenz', value: 62 }
]

export function MesseScreen() {
  return (
    <>
      <Panel at={[0.58, 0.64]} out={[0.84, 0.88]} className='kx-app'>
        <div className='kx-app__phone'>
          <div className='off-talent__notch' />
          <div className='kx-app__brand'>Aivy® Attract</div>
          <div className='kx-app__title'>Deine Matches</div>
          {MATCHES.map((m, i) => (
            <div
              key={m.job}
              className='kx-app__match'
              style={anim({ at: [0.62 + i * 0.04, 0.68 + i * 0.04], y: 12 })}
            >
              {m.top && <em>Passt sehr gut</em>}
              <b>{m.job}</b>
              <small>Passungs-Grad</small>
              <span>
                <i style={{ width: `${m.value}%` }} />
              </span>
              <strong>Mehr</strong>
            </div>
          ))}
        </div>
      </Panel>

      <Caption
        show={[0.04, 0.1]}
        hide={[0.3, 0.34]}
        label='Auf der Messe'
        title='Ab auf die Messe nebenan.'
        short='Mara bringt statt Flyern einen QR-Code für Aivy® Attract mit.'
      >
        Nebenan in der Messehalle ist heute Karrieremesse. Mara geht rüber und
        bringt statt Flyern einen QR-Code für Aivy® Attract mit.
      </Caption>
      <Caption
        show={[0.36, 0.42]}
        hide={[0.56, 0.6]}
        label='Auf der Messe'
        title='Scannen und spielen.'
        short='Ein paar Minuten am Smartphone, ganz ohne Lebenslauf und Termin.'
      >
        Wer am Stand vorbeikommt, scannt den Code und spielt ein paar Minuten am
        Smartphone. Ganz ohne Lebenslauf und Termin.
      </Caption>
      <Caption
        show={[0.6, 0.66]}
        hide={[0.82, 0.86]}
        label='Auf der Messe'
        title='Sofort sehen, was passt.'
        short='Attract zeigt die passenden Stellen. Über „Mehr“ geht es direkt zur Anzeige.'
      >
        Attract zeigt sofort, welche eurer Stellen am besten passen, mit
        Passungs-Grad. Über „Mehr“ geht es direkt zur passenden Stellenanzeige.
      </Caption>
      <Caption
        show={[0.86, 0.9]}
        hide={[0.97, 1]}
        label='Auf der Messe'
        title='Zurück im Büro.'
        short='Die ersten Bewerbungen von der Messe sind schon unterwegs.'
      >
        Zurück im Büro. Die ersten Bewerbungen von der Messe sind schon
        unterwegs.
      </Caption>
    </>
  )
}

export const MESSE = {
  id: 'messe',
  after: 'profil',
  len: 560,
  label: 'Auf der Messe',
  title: 'Auf der Karrieremesse',
  teaser:
    'Mara geht in die Messehalle nebenan. Talente scannen einen QR-Code und sehen mit Aivy® Attract, welche Stelle passt.',
  hidesMara: true,
  camera: (p, base) =>
    cameraAt(
      [
        { p: 0, cam: base },
        { p: 0.07, cam: cam([22, 9, 1], 0.72) },
        { p: 0.26, cam: cam([30, 7.5, 1], 0.8) },
        { p: 0.36, cam: cam([38.5, 6, 1.4], 1.45) },
        { p: 0.56, cam: cam([38.5, 6, 1.4], 1.45) },
        { p: 0.62, cam: cam([38.5, 6, 1.4], 1.35, -0.2) },
        { p: 0.84, cam: cam([38.5, 6, 1.4], 1.35, -0.2) },
        { p: 0.9, cam: cam([24, 8, 1], 0.78) },
        { p: 1, cam: base }
      ],
      p
    ),
  World: MesseWorld,
  Screen: MesseScreen
}
